import { FC, useMemo } from 'react'
import * as styles from 'components/features/socialFeed/FeedUserInfo/FeedUserInfo.styl'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { FeedUserUid, FeedBadge, MatchingPeergroup, PostUid } from 'interfaces/feed'
import { postDate } from 'helpers/dateTimeHelpers'
import { FeedAvatarWithInfo } from 'components/features/socialFeed/FeedAvatarWithInfo/FeedAvatarWithInfo'
import { getSinglePostAnchorId } from 'helpers/feedHelpers'
import { FeedUserBadgesProps } from 'components/features/socialFeed/FeedUserInfo/FeedUserBadges'
import { UserAvatar } from 'components/features/socialFeed/Avatar/UserAvatar'
import { useIsPublicPage } from 'helpers/pageHelpers'
import { Anchor } from 'interfaces/navigation'
import { FeedUserInfoLayout } from 'components/features/socialFeed/FeedUserInfoLayout/FeedUserInfoLayout'

export type FeedUserInfoStateProps = {
  username: string
  badges: FeedBadge[]
}

export type FeedUserInfoOwnProps = {
  // TODO switch PostUid to UID
  postUid?: PostUid
  userUid: FeedUserUid
  matchingPeergroup?: MatchingPeergroup
  mode: 'post' | 'comment'
  created: string
  isEdited: boolean
  isOwn: boolean
  isPostAuthor?: boolean
  isOpportunityReview?: boolean
}

type FeedUserInfoProps =
  & FeedUserInfoStateProps
  & FeedUserInfoOwnProps

const FeedUserInfo: FC<FeedUserInfoProps> = (props) => {
  const {
    userUid, username,
    isOwn, isPostAuthor = false,
    mode,
    badges,
    created, isEdited,
    postUid,
    matchingPeergroup,
    isOpportunityReview,
  } = props

  const timestamp = useMemo(() => {
    return mode === 'post' && postUid ? (
      <NavigationLink
        className={ styles.dateLink }
        to={ getSinglePostAnchorId(postUid) }
      >
        { postDate(created) }
      </NavigationLink>
    ) : postDate(created)
  }, [created, mode, postUid])

  const isPublic = useIsPublicPage()

  const avatarChild = useMemo(() => {
    if (isOwn || !(matchingPeergroup && matchingPeergroup.match)) {
      return (
        <UserAvatar userUid={ userUid } matchRatio={ null } preventDonut={ isPublic } />
      )
    }

    if (isOpportunityReview) {
      return (
        <UserAvatar userUid={ userUid } matchRatio={ matchingPeergroup.match.ratio } />
      )
    }

    return (
      <FeedAvatarWithInfo
        userUid={ userUid }
        username={ username }
        peergroup={ matchingPeergroup }
        mode={ mode }
      />
    )

  }, [isOpportunityReview, isOwn, matchingPeergroup, mode, userUid, username, isPublic])
  const navigationLinkToParams = useMemo<Anchor>(() => ({ anchor: 'feedauthor', id: username }), [username])

  const userNameChild = useMemo(() => (
    <NavigationLink to={ navigationLinkToParams }>
      { username }
    </NavigationLink>
  ), [username, navigationLinkToParams])

  const badgesProps = useMemo<Omit<FeedUserBadgesProps, 'badges' | 'mode'>>(() => ({
    isOwn,
    isPostAuthor,
  }), [isOwn, isPostAuthor])

  const dateChild = useMemo(() => {
    return (
      <>
        { timestamp }
        { isEdited && ' | Edited' }
      </>
    )
  }, [timestamp, isEdited])

  return (
    <FeedUserInfoLayout
      mode={ mode }
      avatarChild={ avatarChild }
      userNameChild={ userNameChild }
      dateChild={ dateChild }
      badges={ badges }
      badgesProps={ badgesProps }
    />
  )
}

export default FeedUserInfo
